import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LEARN } from "../../theme";
import { useBreakpoint } from "../../shell/LearnShell";
import { useDropTarget } from "../../interaction/useDragEngine";
import { ConfettiBurst } from "../../feedback/Celebration";
import {
  LetterTile,
  PopIn,
  RoundCounter,
  ShakeWrap,
  Tile,
  captionPill,
  fitTiles,
  gameShell,
  hasAsset,
  pieceButtonProps,
  seededShuffle,
  useCompleteOnce,
  useDragAndTap,
  useLetterAccent,
  useWrongFlash,
} from "./engineUtils";
import { useArenaMetrics } from "./tapUtils";

const PAD_LETTERS = ["M", "N", "S", "T", "B", "P"];

function wordArt(letter, word, assetName) {
  if (assetName && hasAsset(letter, assetName)) return assetName;
  const guess = `${String(word).charAt(0).toLowerCase()}_word_${String(word).toLowerCase()}`;
  return hasAsset(letter, guess) ? guess : null;
}

/**
 * Normalise every wordBuilder config shape into rounds:
 *   { kind: "build", word, letters[], assetName, note, seed }   — whole word from tiles
 *   { kind: "fill",  word, before, after, answer, choices[], assetName } — one missing piece
 */
function buildRounds(config, letter) {
  const L = String(config.letter || config.targetLetter || letter || "A");
  if (config.puzzles?.length) {
    return config.puzzles.map((p, i) => ({
      kind: "build",
      word: p.word,
      letters: p.letters || String(p.word).toUpperCase().split(""),
      assetName: wordArt(letter, p.word, p.assetName),
      note: p.soundNote,
      seed: p.shuffleSeed || 11 + i,
    }));
  }
  if (config.rounds?.length) {
    return config.rounds.map((r, i) => {
      const word = String(r.word);
      if (r.endings?.length && r.prefix != null) {
        const before = String(r.prefix);
        return { kind: "fill", word, before, after: "", answer: r.answer || word.slice(before.length), choices: r.endings, assetName: wordArt(letter, word), seed: 11 + i };
      }
      if (r.prefix != null || r.suffix != null) {
        const before = String(r.prefix || "");
        const after = String(r.suffix || "");
        return { kind: "fill", word, before, after, answer: r.answer || word.slice(before.length, word.length - after.length), choices: r.choices || [], assetName: wordArt(letter, word), seed: 11 + i };
      }
      if (r.choices?.length) {
        return { kind: "fill", word, before: "", after: word.slice(1), answer: r.answer || word.charAt(0), choices: r.choices, assetName: wordArt(letter, word), seed: 11 + i };
      }
      const ending = String(r.ending || word.slice(1));
      return { kind: "build", word, letters: [L, ...ending.split("")], assetName: wordArt(letter, word), seed: r.shuffleSeed || 11 + i };
    });
  }
  if (config.endings?.length) {
    return config.endings.map((ending, i) => {
      const word = config.builtWords?.[i] || `${L.toLowerCase()}${ending}`;
      return { kind: "fill", word, before: "", after: ending, answer: L, choices: config.choices || [L], assetName: wordArt(letter, word), seed: 11 + i };
    });
  }
  if (config.items?.length) {
    const firsts = [...new Set(config.items.map((it) => String(it.word).charAt(0).toUpperCase()))];
    return config.items.map((it, i) => {
      const word = String(it.word);
      const answer = word.charAt(0).toUpperCase();
      let choices = config.choicesByWord?.[word] || config.choicesByWord?.__default__ || [...firsts];
      if (choices.length < 3) {
        choices = [...choices];
        for (const p of PAD_LETTERS) { if (choices.length >= 3) break; if (!choices.includes(p) && p !== answer) choices.push(p); }
      }
      return { kind: "fill", word, before: "", after: word.slice(1), answer, choices, assetName: wordArt(letter, word, it.assetName), seed: 11 + i };
    });
  }
  return [];
}

const same = (a, b) => String(a).toLowerCase() === String(b).toLowerCase();
const caseLike = (text, sample) => (String(sample) === String(sample).toUpperCase() && /[A-Z]/.test(String(sample)) ? String(text).toUpperCase() : String(text).toLowerCase());

/**
 * Word builder: letter tiles click into a word rail. Build rounds need the
 * next letter in order; fill rounds need the one missing piece. When the
 * word is whole its letters light up in order, the word is spoken, and the
 * next round slides in. Completes after the last round.
 */
export default function WordBuilderGame({ letter, config, speak, sfx, onComplete, onWrong, reduceMotion }) {
  const finish = useCompleteOnce(onComplete);
  const { wrongId, flash } = useWrongFlash();
  const bp = useBreakpoint();
  const compact = bp === "xs";
  const { accent: letterAccent, deep: letterDeep } = useLetterAccent(letter);
  const rootRef = useRef(null);
  const { w, h, tile } = useArenaMetrics(rootRef);
  const rounds = useMemo(() => buildRounds(config, letter), [config, letter]);
  const [ri, setRi] = useState(0);
  const round = rounds[ri];
  const [built, setBuilt] = useState([]);          // build rounds: placed letters
  const [filled, setFilled] = useState(null);      // fill rounds: the chosen piece
  const [tiles, setTiles] = useState([]);          // tray
  const [lit, setLit] = useState(-1);              // celebration: letters lit up to this index
  const [burst, setBurst] = useState(0);
  const timers = useRef([]);
  const doneRef = useRef(false);

  const later = useCallback((fn, ms) => { const t = setTimeout(fn, ms); timers.current.push(t); return t; }, []);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  // New round: lay out the tray, say the word.
  useEffect(() => {
    if (!round) return;
    doneRef.current = false;
    setBuilt([]);
    setFilled(null);
    setLit(-1);
    const src = round.kind === "build" ? round.letters : round.choices;
    setTiles(seededShuffle(src.map((ch, i) => ({ ch, id: `${ch}-${i}` })), round.seed));
    later(() => speak?.(round.note || round.word), ri === 0 ? 900 : 200);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ri]);

  useEffect(() => {
    if (!round && rounds.length) finish();
  }, [round, rounds.length, finish]);

  const cells = useMemo(() => {
    if (!round) return [];
    if (round.kind === "build") return round.letters.map((ch, i) => ({ ch, fixed: false, filled: built[i] != null, shown: built[i] }));
    const sample = round.choices[0] || round.answer;
    return [
      ...round.before.split("").map((ch) => ({ ch: caseLike(ch, sample), fixed: true, filled: true, shown: caseLike(ch, sample) })),
      { ch: round.answer, fixed: false, filled: filled != null, shown: filled, slot: true },
      ...round.after.split("").map((ch) => ({ ch: caseLike(ch, sample), fixed: true, filled: true, shown: caseLike(ch, sample) })),
    ];
  }, [round, built, filled]);

  const slotIndex = round?.kind === "build" ? built.length : cells.findIndex((c) => c.slot);

  const celebrate = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    const n = cells.length;
    const step = reduceMotion ? 0 : 150;
    for (let i = 0; i < n; i += 1) {
      later(() => { setLit(i); sfx?.("pop", { volume: 0.5, rate: 1 + i * 0.08 }); }, i * step + 120);
    }
    later(() => { speak?.(round.word, { interrupt: true }); sfx?.("sparkle", { volume: 0.7 }); setBurst((b) => b + 1); }, n * step + 200);
    later(() => {
      if (ri + 1 >= rounds.length) finish();
      else setRi((x) => x + 1);
    }, n * step + (reduceMotion ? 500 : 1500));
  }, [cells.length, finish, later, reduceMotion, ri, round, rounds.length, sfx, speak]);

  const place = useCallback(
    (tile) => {
      if (!round || doneRef.current) return false;
      const ok = round.kind === "build" ? same(tile.ch, round.letters[built.length]) : same(tile.ch, round.answer);
      if (!ok) {
        flash(tile.id);
        onWrong?.();
        return false;
      }
      sfx?.("snap");
      setTiles((t) => t.filter((x) => x.id !== tile.id));
      if (round.kind === "build") {
        const next = [...built, tile.ch];
        setBuilt(next);
        if (next.length === round.letters.length) celebrate();
      } else {
        setFilled(tile.ch);
        celebrate();
      }
      return true;
    },
    [built, celebrate, flash, onWrong, round, sfx]
  );

  const onDrop = useCallback(
    ({ payload, targetId }) => {
      if (!targetId || !payload) return false;
      return place(payload);
    },
    [place]
  );
  const drag = useDragAndTap({ reduceMotion, sfx, onDrop });
  const slotRef = useDropTarget(drag.registerTarget, "slot", { slot: true });

  if (!round) return <div style={gameShell.root} />;

  const n = cells.length;
  const choiceCount = round.kind === "build" ? round.letters.length : round.choices.length;
  // Letter blocks and rail slots share one size (1.1 tiles) so the picture,
  // rail and choices read as one composition. The picture takes ~2 tiles,
  // the rail one; the choices wrap in what is left and shrink to 0.85 tiles
  // before anything scrolls. A long word shrinks the rail cells to fit.
  const picScale = compact ? 1.5 : 1.9;
  const gapPx = compact ? 12 : 18;
  const captionPx = compact ? 34 : 40;
  const railPad = compact ? 24 : 32;
  const picBlock = tile ? tile * picScale + captionPx + gapPx : 0;
  const railBlock = tile ? tile * 1.1 + railPad + gapPx : 0;
  const fit = fitTiles({
    count: choiceCount,
    w: w ? w - 16 : 0,
    h: h ? h - picBlock - railBlock - 16 - (compact ? 30 : 0) : 0,
    tile,
    max: 1.1,
    min: 0.85,
    gap: compact ? 0.14 : 0.18,
  });
  const PIECE = fit.scale;
  const cellSize = `min(calc(var(--tile) * ${PIECE}), calc((var(--arena-w, 400px) - ${railPad + 32}px) / ${n} - 6px))`;
  const renderGhost = (p) => <LetterTile text={p.ch} scale={PIECE} />;

  return (
    <div ref={rootRef} style={{ ...gameShell.root, gap: compact ? 8 : 12 }}>
      <ConfettiBurst key={burst} active={burst > 0} originY={30} count={24} reduceMotion={reduceMotion} />
      <RoundCounter done={ri} total={rounds.length} accent={letterAccent} />
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={`${ri}-${round.word}`}
          initial={reduceMotion ? false : { x: 60, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={reduceMotion ? undefined : { x: -60, opacity: 0, transition: { duration: 0.18 } }}
          transition={{ type: "spring", stiffness: 300, damping: 26 }}
          style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflowY: fit.fits ? "hidden" : "auto", overflowX: "hidden", paddingTop: compact ? 30 : 0 }}
        >
        {/* margin:auto centres the composition and still scrolls from the top if it ever overflows */}
        <div style={{ margin: "auto", width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: gapPx, padding: "4px 0" }}>
          {round.assetName && (
            <PopIn reduceMotion={reduceMotion} style={{ flexDirection: "column", alignItems: "center", gap: 0 }}>
              <button
                type="button"
                className="learn-piece"
                aria-label={`Hear ${round.word}`}
                onClick={() => { sfx?.("pop", { volume: 0.5 }); speak?.(round.word, { interrupt: true }); }}
                style={{ background: "transparent", border: "none", padding: 0, cursor: "pointer", borderRadius: `calc(var(--tile) * ${picScale} * 0.24)` }}
              >
                <Tile letter={letter} assetName={round.assetName} alt={round.word} scale={picScale} lift={false} solved={lit >= n - 1} />
              </button>
              <span
                style={{
                  ...captionPill({ accent: letterAccent, deep: letterDeep, size: compact ? "sm" : "md" }),
                  marginTop: -14, position: "relative", zIndex: 2,
                  color: lit >= n - 1 ? LEARN.color.mintDeep : LEARN.color.ink,
                  borderColor: lit >= n - 1 ? `${LEARN.color.mint}88` : `${letterAccent}33`,
                  transition: "color 200ms ease, border-color 200ms ease",
                }}
              >
                {round.word}
              </span>
            </PopIn>
          )}

          {/* Word rail */}
          <div
            style={{
              display: "flex", gap: compact ? 6 : 8, alignItems: "center", justifyContent: "center",
              padding: compact ? "12px 14px" : "16px 20px",
              background: "rgba(255,255,255,0.92)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
              borderRadius: LEARN.radius.lg, boxShadow: LEARN.shadow.card, border: `2px solid ${LEARN.color.line}`,
              maxWidth: "100%", boxSizing: "border-box",
            }}
            aria-label={`Word: ${round.word}`}
          >
            {cells.map((c, i) => {
              const isSlot = i === slotIndex && !c.filled;
              const hover = isSlot && drag.hoverId === "slot";
              const isLit = i <= lit;
              const ghost = c.fixed && !isLit;
              return (
                <motion.div
                  key={i}
                  ref={isSlot ? slotRef : undefined}
                  role={isSlot ? "button" : undefined}
                  aria-label={isSlot ? "Drop the letter here" : undefined}
                  className={isSlot ? "learn-slot-armed" : undefined}
                  animate={isLit && !reduceMotion ? { scale: [1, 1.18, 1], y: [0, -8, 0] } : { scale: 1, y: 0 }}
                  transition={{ duration: 0.34, ease: LEARN.motion.pop }}
                  style={{
                    "--accent": LEARN.color.blue,
                    "--accent-soft": `${LEARN.color.blue}22`,
                    "--accent-ring": `${LEARN.color.blue}33`,
                    width: cellSize, height: cellSize, borderRadius: `calc(${cellSize} * 0.22)`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontFamily: LEARN.font, fontWeight: 700, lineHeight: 1,
                    fontSize: `clamp(24px, calc(${cellSize} * 0.46), 44px)`,
                    color: c.filled ? (ghost ? LEARN.color.ink : "#fff") : "transparent",
                    background: c.filled
                      ? ghost ? "linear-gradient(180deg, #FFFFFF 0%, #F5F8FD 100%)" : isLit ? LEARN.color.mint : LEARN.color.blue
                      : hover ? `${LEARN.color.blue}22` : "rgba(11,26,51,0.04)",
                    border: c.filled
                      ? `2px solid ${ghost ? LEARN.color.line : "transparent"}`
                      : `3px ${hover ? "solid" : "dashed"} ${isSlot ? (hover ? LEARN.color.blue : LEARN.color.sun) : LEARN.color.line}`,
                    boxShadow: c.filled
                      ? ghost ? `0 4px 0 ${LEARN.color.line}, ${LEARN.shadow.card}` : `0 4px 0 ${isLit ? LEARN.color.mintDeep : LEARN.color.blueDeep}, ${LEARN.shadow.card}`
                      : hover ? `0 0 0 6px ${LEARN.color.blue}33` : "none",
                    textShadow: c.filled && !ghost ? "0 2px 0 rgba(11,26,51,0.18)" : "none",
                    transition: "background 160ms ease, color 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
                    flexShrink: 0,
                    boxSizing: "border-box",
                    position: "relative",
                  }}
                >
                  {c.filled ? c.shown : ""}
                </motion.div>
              );
            })}
          </div>

          {/* Tray: letter blocks in a fixed column count from the fit so rows stay aligned as tiles leave. */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${fit.cols}, max-content)`,
              justifyContent: "center", alignItems: "center",
              gap: `calc(var(--tile) * ${compact ? 0.14 : 0.18})`,
              width: "100%", flexShrink: 0, padding: "6px 0 4px",
            }}
            aria-label="Letter tiles"
          >
            <AnimatePresence initial={false}>
              {tiles.map((t, i) => (
                <PopIn key={t.id} index={i} reduceMotion={reduceMotion} layout exit={reduceMotion ? undefined : { scale: 0.3, opacity: 0, transition: { duration: 0.14 } }} style={{ justifyContent: "center" }}>
                  <ShakeWrap shake={wrongId === t.id} reduceMotion={reduceMotion} style={{ borderRadius: `calc(var(--tile) * ${PIECE} * 0.22)` }}>
                    <button
                      {...pieceButtonProps({
                        dragProps: drag.dragProps,
                        payload: t,
                        render: renderGhost,
                        label: `Letter ${t.ch}`,
                        onTap: () => {
                          sfx?.("pop", { volume: 0.5 });
                          // A tap places straight into the open slot (there is only one);
                          // say the letter unless the word just finished (it is read aloud).
                          if (place(t) && !doneRef.current) speak?.(t.ch, { interrupt: true });
                        },
                      })}
                    >
                      <LetterTile text={t.ch} scale={PIECE} />
                    </button>
                  </ShakeWrap>
                </PopIn>
              ))}
            </AnimatePresence>
          </div>
        </div>
        </motion.div>
      </AnimatePresence>
      <drag.GhostLayer />
    </div>
  );
}
